import { useState } from "react";
import { useRoute, Link } from "wouter";
import {
  ArrowLeft,
  Calendar,
  Car,
  Check,
  Hash,
  ListOrdered,
  MapPin,
  PackagePlus,
  Plane,
  RefreshCw,
  Route,
  User,
  UserRound,
  Briefcase,
  Languages,
  AlertCircle,
  AlertTriangle,
  Truck,
  XCircle,
} from "lucide-react";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "../../../server/routers";
import { trpc, queryClient } from "../lib/trpc";

type OrderGetOutput = inferRouterOutputs<AppRouter>["order"]["get"];
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input, Select } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Separator } from "../components/ui/separator";
import { OrderStatusBadge } from "../components/order/OrderStatusBadge";
import { OrderStatusTracker } from "../components/order/OrderStatusTracker";
import { fmtCurrency, fmtDateTime } from "../lib/format";
import { canMarkCompleteAt, isDriverInfoOverdue } from "../../../shared/order-rules";
import { SERVICE_TYPE_META } from "../../../shared/const";
import { zoneByCode } from "../../../shared/booking";

const QUOTE_SOURCE_LABEL = {
  system_estimate: { "zh-TW": "系統粗估", "en-US": "System estimate", "zh-CN": "系统粗估" },
  dispatcher_adjusted: { "zh-TW": "調度員報價", "en-US": "Dispatcher quote", "zh-CN": "调度员报价" },
  dispatcher_manual: { "zh-TW": "人工報價", "en-US": "Manual quote", "zh-CN": "人工报价" },
  agency_rejection: { "zh-TW": "需求方退回", "en-US": "Agency rejected", "zh-CN": "需求方退回" },
  agency_accepted: { "zh-TW": "需求方確認", "en-US": "Agency accepted", "zh-CN": "需求方确认" },
} as const;

export default function OrderDetail() {
  const [match, params] = useRoute<{ id: string }>("/orders/:id");
  const id = match ? Number(params.id) : 0;
  const { user } = useAuth();
  const { t } = useLanguage();

  const q = trpc.order.get.useQuery({ id }, { enabled: id > 0 });

  if (!match) return null;
  if (q.isLoading) return <div className="h-32 animate-pulse rounded-lg bg-muted/40" />;
  if (q.error) {
    return (
      <div className="mx-auto max-w-2xl">
        <Card>
          <CardContent className="space-y-3 p-8 text-center">
            <AlertCircle className="mx-auto h-6 w-6 text-destructive" />
            <p className="font-display text-lg">{q.error.message}</p>
            <Link href="/orders">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-3.5 w-3.5" />
                {t({ "zh-TW": "回訂單列表", "en-US": "Back to orders", "zh-CN": "回订单列表" })}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!q.data || !user) return null;

  const { order, quotes } = q.data;
  const role = user.role;

  // 金額主標：需求方/調度員看報價總額；供應方（finalPrice 已被後端裁剪為 null）看車隊實得
  const headlineAmount = order.finalPrice ?? order.fleetPayout ?? quotes[quotes.length - 1]?.price ?? null;
  const headlineCurrency = order.finalPrice
    ? order.currency
    : order.fleetPayout
      ? (order.fleetPayoutCurrency ?? order.currency)
      : order.currency;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* breadcrumb */}
      <Link href="/orders">
        <button className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" />
          {t({ "zh-TW": "回訂單列表", "en-US": "Back to orders", "zh-CN": "回订单列表" })}
        </button>
      </Link>

      {/* header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <p className="font-mono text-[12px] text-muted-foreground">{order.orderNumber}</p>
            <OrderStatusBadge status={order.status} />
            {order.createdVia === "dispatcher_proxy" && (
              <span className="rounded bg-primary-soft px-1.5 py-0.5 text-[11px] font-medium text-primary-soft-foreground">
                {t({ "zh-TW": "平台代建", "en-US": "By platform", "zh-CN": "平台代建" })}
              </span>
            )}
            <span className="font-mono text-[12px] uppercase tracking-wider text-muted-foreground">
              {order.region === "taiwan" ? "TW" : "HK"} · {t(SERVICE_TYPE_META[order.serviceType].label)}
            </span>
          </div>
          <h1 className="font-display text-3xl font-medium tracking-tight">
            {fmtCurrency(headlineAmount, headlineCurrency)}
          </h1>
          <p className="text-[13px] text-muted-foreground">
            {t({ "zh-TW": "建立於", "en-US": "Created", "zh-CN": "建立于" })} {fmtDateTime(order.createdAt)}
          </p>
        </div>

        <ActionPanel order={order} quotes={quotes} role={user.role} reload={() => q.refetch()} />
      </div>

      <OrderStatusTracker status={order.status} pickupTime={order.pickupTime} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
        {/* left: details */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t({ "zh-TW": "用車資訊", "en-US": "Trip details", "zh-CN": "用车信息" })}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <DetailRow icon={Calendar} label={t({ "zh-TW": "上車時間", "en-US": "Pick-up time", "zh-CN": "上车时间" })}>
                {fmtDateTime(order.pickupTime)}
              </DetailRow>
              {order.bookingMode && (
                <DetailRow icon={Plane} label={t({ "zh-TW": "預約方式", "en-US": "Booking mode", "zh-CN": "预约方式" })}>
                  {order.bookingMode === "by_flight"
                    ? t({ "zh-TW": "依航班", "en-US": "By flight", "zh-CN": "依航班" })
                    : t({ "zh-TW": "依指定時間", "en-US": "By scheduled time", "zh-CN": "依指定时间" })}
                  {order.bookingMode === "by_flight" && (
                    <span className="block text-[12px] text-muted-foreground">
                      {t({
                        "zh-TW": "含航班落地後 1 小時免費等候，逾時費用線下計收。",
                        "en-US": "Includes 1-hour wait after landing; overtime billed offline.",
                        "zh-CN": "含航班落地后 1 小时免费等候，逾时费用线下计收。",
                      })}
                    </span>
                  )}
                </DetailRow>
              )}
              <DetailRow icon={MapPin} label={t({ "zh-TW": "上車地點", "en-US": "Pick-up", "zh-CN": "上车地点" })}>
                {order.pickupLocation}
              </DetailRow>
              {order.dropoffLocation && (
                <DetailRow icon={Route} label={t({ "zh-TW": "下車地點", "en-US": "Drop-off", "zh-CN": "下车地点" })}>
                  {order.dropoffLocation}
                </DetailRow>
              )}
              {!!order.stops?.length && (
                <DetailRow
                  icon={ListOrdered}
                  label={
                    order.serviceType === "airport_pickup"
                      ? t({ "zh-TW": "目的地（依序）", "en-US": "Destinations (in order)", "zh-CN": "目的地（依序）" })
                      : order.serviceType === "airport_dropoff"
                        ? t({ "zh-TW": "上車點（依序）", "en-US": "Pick-up points (in order)", "zh-CN": "上车点（依序）" })
                        : t({ "zh-TW": "停點（依序）", "en-US": "Stops (in order)", "zh-CN": "停点（依序）" })
                  }
                >
                  <ol className="space-y-1">
                    {order.stops.map((s) => {
                      const zone = s.zone ? zoneByCode(s.zone) : undefined;
                      return (
                        <li key={s.seq} className="flex items-baseline gap-2">
                          <span className="numeric font-mono text-[12px] text-muted-foreground">{s.seq}.</span>
                          <span>{s.location}</span>
                          {zone && (
                            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[12px] uppercase tracking-wider text-muted-foreground">
                              {t(zone.label)}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </DetailRow>
              )}
              {order.flightNumber && (
                <DetailRow icon={Plane} label={t({ "zh-TW": "航班", "en-US": "Flight", "zh-CN": "航班" })}>
                  {order.airport ? `${order.airport} · ` : ""}
                  {order.flightNumber}
                </DetailRow>
              )}
              {order.charterDurationHours && (
                <DetailRow icon={Route} label={t({ "zh-TW": "包車時數", "en-US": "Charter hours", "zh-CN": "包车时数" })}>
                  {order.charterDurationHours} {t({ "zh-TW": "小時", "en-US": "hours", "zh-CN": "小时" })}
                  {order.charterItinerary && (
                    <span className="block text-[12px] text-muted-foreground">{order.charterItinerary}</span>
                  )}
                </DetailRow>
              )}
              <Separator />
              <DetailRow icon={User} label={t({ "zh-TW": "乘客", "en-US": "Passenger", "zh-CN": "乘客" })}>
                {order.passengerName} · {order.passengerCount} {t({ "zh-TW": "人", "en-US": "pax", "zh-CN": "人" })}
                {order.luggageCount ? ` · ${order.luggageCount} ${t({ "zh-TW": "件行李", "en-US": "luggage", "zh-CN": "件行李" })}` : ""}
                {order.passengerPhone && <span className="block text-[12px] text-muted-foreground">{order.passengerPhone}</span>}
                {order.passengerWhatsapp && <span className="block text-[12px] text-muted-foreground">WhatsApp：{order.passengerWhatsapp}</span>}
                {order.passengerLine && <span className="block text-[12px] text-muted-foreground">LINE：{order.passengerLine}</span>}
                {order.passengerWechat && <span className="block text-[12px] text-muted-foreground">WeChat：{order.passengerWechat}</span>}
                {order.passengerKakao && <span className="block text-[12px] text-muted-foreground">Kakao Talk：{order.passengerKakao}</span>}
                {order.passengerAltContact && (
                  <span className="block text-[12px] text-muted-foreground">
                    {t({ "zh-TW": "其他聯絡：", "en-US": "Alt contact: ", "zh-CN": "其他联络：" })}
                    {order.passengerAltContact}
                  </span>
                )}
              </DetailRow>
              <DetailRow icon={Briefcase} label={t({ "zh-TW": "車款", "en-US": "Vehicle", "zh-CN": "车款" })}>
                {order.vehicleModelId ? (
                  <span className="flex items-center gap-3">
                    {order.vehicleModelPhotoUrl ? (
                      <img
                        src={order.vehicleModelPhotoUrl}
                        alt={order.vehicleModelName ?? order.vehicleType}
                        className="aspect-[5/2] w-24 shrink-0 rounded-md border border-border object-cover"
                      />
                    ) : (
                      <span className="flex aspect-[5/2] w-24 shrink-0 items-center justify-center rounded-md border border-border bg-muted/50">
                        <Car className="h-5 w-5 text-muted-foreground/60" strokeWidth={1.5} />
                      </span>
                    )}
                    <span>{order.vehicleModelName ?? order.vehicleType}</span>
                  </span>
                ) : (
                  order.vehicleType
                )}
              </DetailRow>
              {!!order.addons?.length && (
                <DetailRow icon={PackagePlus} label={t({ "zh-TW": "加購服務", "en-US": "Add-ons", "zh-CN": "加购服务" })}>
                  <ul className="space-y-0.5">
                    {order.addons.map((a) => (
                      <li key={a.code} className="flex items-baseline justify-between gap-3">
                        <span>{a.name}</span>
                        {role === "dispatcher" || role === "admin" ? (
                          <span className="numeric font-mono text-[12px] text-muted-foreground">
                            {fmtCurrency(a.priceHkd, "HKD")}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  {role === "agency" && (
                    <span className="mt-1 block text-[12px] text-muted-foreground">
                      {t({ "zh-TW": "加購費用已含於總價。", "en-US": "Add-on fees are included in the total.", "zh-CN": "加购费用已含于总价。" })}
                    </span>
                  )}
                </DetailRow>
              )}
              {order.specialRequests && (
                <DetailRow icon={Languages} label={t({ "zh-TW": "特殊需求", "en-US": "Special requests", "zh-CN": "特殊需求" })}>
                  <span className="whitespace-pre-wrap">{order.specialRequests}</span>
                </DetailRow>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t({ "zh-TW": "報價歷程", "en-US": "Quote history", "zh-CN": "报价历程" })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {quotes.map((qq) => (
                  <li key={qq.id} className="flex items-start gap-3 border-l-2 border-border pl-3">
                    <div className="flex-1 space-y-0.5">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="text-[13px] font-medium">{t(QUOTE_SOURCE_LABEL[qq.source])}</p>
                        <p className="numeric font-mono text-[12px] text-muted-foreground">
                          {qq.price ? fmtCurrency(qq.price, qq.currency) : "—"}
                        </p>
                      </div>
                      <p className="text-[12px] text-muted-foreground">
                        {qq.actorName ?? "—"} · {fmtDateTime(qq.createdAt)}
                      </p>
                      {qq.notes && <p className="mt-1 text-[12px] text-foreground/80">{qq.notes}</p>}
                      {qq.rejectionReason && (
                        <p className="mt-1 rounded border border-destructive/30 bg-destructive-soft px-2 py-1 text-[12px] text-destructive">
                          {qq.rejectionReason}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>

        {/* right: parties + price breakdown */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t({ "zh-TW": "合作方", "en-US": "Parties", "zh-CN": "合作方" })}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="font-mono text-[12px] uppercase tracking-wider text-muted-foreground">
                  {t({ "zh-TW": "需求方", "en-US": "Agency", "zh-CN": "需求方" })}
                </p>
                <p className="text-[14px] font-medium">{order.agencyName ?? "—"}</p>
              </div>
              <div>
                <p className="font-mono text-[12px] uppercase tracking-wider text-muted-foreground">
                  {t({ "zh-TW": "供應方", "en-US": "Fleet", "zh-CN": "供应方" })}
                </p>
                <p className="text-[14px] font-medium">
                  {order.fleetName ?? (
                    <span className="text-muted-foreground italic">{t({ "zh-TW": "尚未指派", "en-US": "Not assigned", "zh-CN": "尚未指派" })}</span>
                  )}
                </p>
              </div>
              {order.fleetRejectedReason && (
                <div className="rounded border border-destructive/30 bg-destructive-soft px-2 py-1.5 text-[12px] text-destructive">
                  <span className="font-medium">
                    {t({ "zh-TW": "上次拒絕原因：", "en-US": "Last rejection: ", "zh-CN": "上次拒绝原因：" })}
                  </span>
                  {order.fleetRejectedReason}
                </div>
              )}
            </CardContent>
          </Card>

          <DriverInfoCard key={order.id} order={order} role={role} onDone={() => { queryClient.invalidateQueries(); q.refetch(); }} />

          {(order.finalPrice || order.fleetPayout) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {user.role === "agency"
                    ? t({ "zh-TW": "應付金額", "en-US": "Amount payable", "zh-CN": "应付金额" })
                    : user.role === "fleet"
                      ? t({ "zh-TW": "應收金額", "en-US": "Amount receivable", "zh-CN": "应收金额" })
                      : t({ "zh-TW": "金額拆解", "en-US": "Breakdown", "zh-CN": "金额拆解" })}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-[13px]">
                {user.role === "agency" || user.role === "fleet" ? (
                  // 需求方/供應方：只見單一數字（應付/應收），不見平台拆帳
                  <div className="flex items-baseline justify-between">
                    <span className="text-muted-foreground">{t({ "zh-TW": "合計", "en-US": "Total", "zh-CN": "合计" })}</span>
                    <span className="numeric text-base font-medium text-foreground">{fmtCurrency(headlineAmount, headlineCurrency)}</span>
                  </div>
                ) : order.priceHkd != null ? (
                  // 平台方（管理員/調度員）— 新單（港幣中心制）：客付顯示幣別＋HKD 權威價拆解
                  <>
                    {order.finalPrice && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t({ "zh-TW": "客付（需求方應付）", "en-US": "Customer pays", "zh-CN": "客付（需求方应付）" })}</span>
                        <span className="numeric font-medium">{fmtCurrency(order.finalPrice, order.currency)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-[12px] text-muted-foreground">
                      <span>
                        {order.currency !== "HKD" && order.exchangeRateSnapshot
                          ? t({
                              "zh-TW": `權威價（匯率快照 1 HKD = ${Number(order.exchangeRateSnapshot)} ${order.currency}）`,
                              "en-US": `Authoritative (rate snapshot 1 HKD = ${Number(order.exchangeRateSnapshot)} ${order.currency})`,
                              "zh-CN": `权威价（汇率快照 1 HKD = ${Number(order.exchangeRateSnapshot)} ${order.currency}）`,
                            })
                          : t({ "zh-TW": "權威價", "en-US": "Authoritative price", "zh-CN": "权威价" })}
                      </span>
                      <span className="numeric">≈ {fmtCurrency(order.priceHkd, "HKD")}</span>
                    </div>
                    {order.platformFee && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t({ "zh-TW": "平台抽成", "en-US": "Platform fee", "zh-CN": "平台抽成" })}</span>
                        <span className="numeric">−{fmtCurrency(order.platformFee, "HKD")}</span>
                      </div>
                    )}
                    {order.fleetPayout && (
                      <>
                        <Separator />
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t({ "zh-TW": "車隊實得", "en-US": "Fleet payout", "zh-CN": "车队实得" })}</span>
                          <span className="numeric font-medium text-foreground">
                            {fmtCurrency(order.fleetPayout, order.fleetPayoutCurrency ?? "HKD")}
                          </span>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  // 平台方 — 歷史單（無 priceHkd）：沿用舊顯示（單一幣別拆解）
                  <>
                    {order.finalPrice && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t({ "zh-TW": "報價總額", "en-US": "Quoted price", "zh-CN": "报价总额" })}</span>
                        <span className="numeric font-medium">{fmtCurrency(order.finalPrice, order.currency)}</span>
                      </div>
                    )}
                    {order.platformFee && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t({ "zh-TW": "平台抽成", "en-US": "Platform fee", "zh-CN": "平台抽成" })}</span>
                        <span className="numeric">−{fmtCurrency(order.platformFee, order.currency)}</span>
                      </div>
                    )}
                    {order.fleetPayout && (
                      <>
                        {order.finalPrice && <Separator />}
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t({ "zh-TW": "車隊實得", "en-US": "Fleet payout", "zh-CN": "车队实得" })}</span>
                          <span className="numeric font-medium text-foreground">
                            {fmtCurrency(order.fleetPayout, order.fleetPayoutCurrency ?? order.currency)}
                          </span>
                        </div>
                      </>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {order.dispatcherNotes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t({ "zh-TW": "調度員備註", "en-US": "Dispatcher notes", "zh-CN": "调度员备注" })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-[13px] text-foreground/80">{order.dispatcherNotes}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Calendar;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
      <div className="min-w-0 flex-1">
        <p className="font-mono text-[12px] uppercase tracking-wider text-muted-foreground">{label}</p>
        <div className="text-[14px] text-foreground/90">{children}</div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Action panel：依角色 + 狀態顯示對應動作
   ───────────────────────────────────────────────────────────── */
function ActionPanel({
  order,
  quotes,
  role,
  reload,
}: {
  order: NonNullable<OrderGetOutput["order"]>;
  quotes: OrderGetOutput["quotes"];
  role: "admin" | "dispatcher" | "agency" | "fleet";
  reload: () => void;
}) {
  const { t } = useLanguage();
  const status = order.status;
  const invalidate = () => {
    queryClient.invalidateQueries();
    reload();
  };

  // agency 動作
  if (role === "agency") {
    if (status === "quoted") {
      return (
        <div className="flex gap-2">
          <RejectDialog orderId={order.id} mode="quote" onDone={invalidate} />
          <AgencyAccept orderId={order.id} onDone={invalidate} />
        </div>
      );
    }
    // D3：確認售價（confirmed）後需求方不可取消，只留平台方（調度/管理員）
    if (["pending", "quoting", "rejected_quote"].includes(status)) {
      return <CancelButton orderId={order.id} onDone={invalidate} />;
    }
    return null;
  }

  // fleet 動作（20260611 反饋批次：車隊僅能確認接單，拒絕鈕移除 — 無法承接請聯繫平台調度）
  if (role === "fleet") {
    if (status === "dispatching") {
      return (
        <div className="flex items-center gap-3">
          <p className="text-[12px] text-muted-foreground">
            {t({
              "zh-TW": "如無法接單，請聯繫平台調度。",
              "en-US": "If you cannot take this order, please contact the platform dispatcher.",
              "zh-CN": "如无法接单，请联系平台调度。",
            })}
          </p>
          <FleetConfirm orderId={order.id} onDone={invalidate} />
        </div>
      );
    }
    if (status === "active") {
      return <CompleteButton orderId={order.id} pickupTime={order.pickupTime} onDone={invalidate} />;
    }
    return null;
  }

  // dispatcher / admin
  if (["pending", "quoting", "rejected_quote", "quoted"].includes(status)) {
    const lastPrice = quotes.length ? Number(quotes[quotes.length - 1]?.price ?? 0) : 0;
    return (
      <div className="flex gap-2">
        <CancelButton orderId={order.id} onDone={invalidate} />
        <DispatcherQuote orderId={order.id} initialPrice={lastPrice} onDone={invalidate} />
      </div>
    );
  }
  if (["confirmed", "rejected_dispatch"].includes(status)) {
    return (
      <div className="flex gap-2">
        <CancelButton orderId={order.id} onDone={invalidate} />
        <DispatcherAssign orderId={order.id} region={order.region} onDone={invalidate} />
      </div>
    );
  }
  // 20260611 §2.4：車隊不可拒單，改派由平台方（調度員/管理員）執行 — 限出發前
  if (status === "dispatching") {
    return (
      <div className="flex gap-2">
        <CancelButton orderId={order.id} onDone={invalidate} />
        <ReassignFleet order={order} onDone={invalidate} />
      </div>
    );
  }
  if (status === "active") {
    return (
      <div className="flex gap-2">
        <CancelButton orderId={order.id} onDone={invalidate} />
        <ReassignFleet order={order} onDone={invalidate} />
        <CompleteButton orderId={order.id} pickupTime={order.pickupTime} onDone={invalidate} />
      </div>
    );
  }
  return null;
}

/* ── 報價（dispatcher） ─────────────────────────────────────── */
function DispatcherQuote({ orderId, initialPrice, onDone }: { orderId: number; initialPrice: number; onDone: () => void }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState(initialPrice || 0);
  const [notes, setNotes] = useState("");
  // 車隊成本參考（reference-only）：協助訂出高於成本的客人售價
  const costRefQ = trpc.fleetCost.referenceForOrder.useQuery({ orderId }, { enabled: open });
  const costRefs = costRefQ.data ?? [];
  const costRange = costRefs.length
    ? {
        currency: costRefs[0]!.currency,
        min: Math.min(...costRefs.map((c) => c.total)),
        max: Math.max(...costRefs.map((c) => c.total)),
        n: costRefs.length,
      }
    : null;
  const m = trpc.quote.submit.useMutation({
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Check className="h-3.5 w-3.5" />
          {t({ "zh-TW": "送出報價", "en-US": "Submit quote", "zh-CN": "送出报价" })}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t({ "zh-TW": "送出報價", "en-US": "Submit quote", "zh-CN": "送出报价" })}</DialogTitle>
          <DialogDescription>
            {t({ "zh-TW": "送出後需求方會收到通知，需確認或退回。", "en-US": "Agency will be notified to accept or reject.", "zh-CN": "送出后需求方会收到通知，需确认或退回。" })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {costRange && (
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-[12px] text-muted-foreground">
              {t({ "zh-TW": "車隊成本參考", "en-US": "Fleet cost reference", "zh-CN": "车队成本参考" })}：
              <span className="numeric font-medium text-foreground">
                {costRange.currency} {costRange.min.toLocaleString()}
                {costRange.max !== costRange.min ? `–${costRange.max.toLocaleString()}` : ""}
              </span>
              （{costRange.n} {t({ "zh-TW": "家車隊", "en-US": "fleets", "zh-CN": "家车队" })}）
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{t({ "zh-TW": "最終報價金額", "en-US": "Final price", "zh-CN": "最终报价金额" })}</Label>
            <Input type="number" value={price} min={1} onChange={(e) => setPrice(Number(e.target.value))} />
          </div>
          <div className="space-y-1.5">
            <Label>{t({ "zh-TW": "備註（可選）", "en-US": "Notes (optional)", "zh-CN": "备注（可选）" })}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {m.error && <p className="text-[12px] text-destructive">{m.error.message}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}
          </Button>
          <Button onClick={() => m.mutate({ orderId, price, notes: notes || undefined })} disabled={m.isPending}>
            {t({ "zh-TW": "送出", "en-US": "Submit", "zh-CN": "送出" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── 派遣（dispatcher） ─────────────────────────────────────── */
function DispatcherAssign({ orderId, region, onDone }: { orderId: number; region: "taiwan" | "hongkong"; onDone: () => void }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [fleetId, setFleetId] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const fleetsQ = trpc.party.listActiveFleets.useQuery({ region }, { enabled: open });
  // 參考成本（reference-only）：該單區域 + 車型的車隊成本
  const costRefQ = trpc.fleetCost.referenceForOrder.useQuery({ orderId }, { enabled: open });
  const costByFleet = new Map<number, { total: number; currency: string }>();
  (costRefQ.data ?? []).forEach((c) => costByFleet.set(c.fleetId, { total: c.total, currency: c.currency }));
  const m = trpc.dispatch.assign.useMutation({
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Truck className="h-3.5 w-3.5" />
          {t({ "zh-TW": "指派車隊", "en-US": "Assign fleet", "zh-CN": "指派车队" })}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t({ "zh-TW": "指派車隊", "en-US": "Assign fleet", "zh-CN": "指派车队" })}</DialogTitle>
          <DialogDescription>
            {t({ "zh-TW": "選擇一個活躍的車隊。指派後車隊會收到通知並於系統內確認接單。", "en-US": "Pick an active fleet. They'll be notified to confirm the dispatch.", "zh-CN": "选择一个活跃的车队。指派后车队会收到通知并于系统内确认接单。" })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t({ "zh-TW": "車隊", "en-US": "Fleet", "zh-CN": "车队" })}</Label>
            <Select value={fleetId ?? ""} onChange={(e) => setFleetId(Number(e.target.value) || null)}>
              <option value="">{t({ "zh-TW": "— 選擇車隊 —", "en-US": "— pick fleet —", "zh-CN": "— 选择车队 —" })}</option>
              {(fleetsQ.data ?? []).map((f) => {
                const c = costByFleet.get(f.id);
                const costLabel = c
                  ? `${t({ "zh-TW": "成本", "en-US": "cost", "zh-CN": "成本" })} ${c.currency} ${c.total.toLocaleString()}`
                  : t({ "zh-TW": "成本未設定", "en-US": "cost unset", "zh-CN": "成本未设定" });
                return (
                  <option key={f.id} value={f.id}>
                    {f.companyName}（{f.contactPerson ?? "—"}）· {costLabel}
                  </option>
                );
              })}
            </Select>
            <p className="text-[12px] text-muted-foreground">
              {t({ "zh-TW": "成本為該車隊對此車型的參考價（供內部派遣決策，不對外顯示）。", "en-US": "Cost is this fleet's reference price for the vehicle type (internal only).", "zh-CN": "成本为该车队对此车型的参考价（供内部派遣决策，不对外显示）。" })}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>{t({ "zh-TW": "備註（可選）", "en-US": "Notes (optional)", "zh-CN": "备注（可选）" })}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {m.error && <p className="text-[12px] text-destructive">{m.error.message}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>{t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}</Button>
          <Button
            onClick={() => fleetId && m.mutate({ orderId, fleetId, notes: notes || undefined })}
            disabled={!fleetId || m.isPending}
          >
            {t({ "zh-TW": "指派", "en-US": "Assign", "zh-CN": "指派" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── 更換車隊（dispatcher/admin；20260611 §2.4 改派由平台執行）── */
function ReassignFleet({
  order,
  onDone,
}: {
  order: NonNullable<OrderGetOutput["order"]>;
  onDone: () => void;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [fleetId, setFleetId] = useState<number | null>(null);
  const [reason, setReason] = useState("");
  const [justDone, setJustDone] = useState(false);
  const fleetsQ = trpc.party.listActiveFleets.useQuery({ region: order.region }, { enabled: open });
  // 排除現任車隊（同 region、active）
  const candidates = (fleetsQ.data ?? []).filter((f) => f.id !== order.fleetId);
  const m = trpc.dispatch.reassign.useMutation({
    onSuccess: () => {
      setOpen(false);
      setJustDone(true);
      setTimeout(() => setJustDone(false), 4000);
      onDone();
    },
  });
  const onOpenChange = (v: boolean) => {
    setOpen(v);
    if (v) {
      // 開啟時重置草稿（避免殘留上次選擇/錯誤訊息）
      setFleetId(null);
      setReason("");
      m.reset();
    }
  };

  // 出發後不可改派（server 亦擋）：不顯示按鈕
  if (order.pickupTime <= Date.now()) return null;

  return (
    <div className="flex items-center gap-2">
      {justDone && (
        <span className="flex items-center gap-1 text-[12px] text-[var(--color-success)]">
          <Check className="h-3.5 w-3.5" />
          {t({ "zh-TW": "已更換車隊", "en-US": "Fleet reassigned", "zh-CN": "已更换车队" })}
        </span>
      )}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogTrigger asChild>
          <Button variant="outline">
            <RefreshCw className="h-3.5 w-3.5" />
            {t({ "zh-TW": "更換車隊", "en-US": "Reassign fleet", "zh-CN": "更换车队" })}
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t({ "zh-TW": "更換承接車隊", "en-US": "Reassign fleet", "zh-CN": "更换承接车队" })}</DialogTitle>
            <DialogDescription>
              {t({
                "zh-TW": "限出發前。換隊後訂單回到待車隊確認，已填司機資訊將清空，並通知新舊車隊與需求方。",
                "en-US": "Before departure only. The order returns to pending fleet confirmation, driver info is cleared, and all parties are notified.",
                "zh-CN": "限出发前。换队后订单回到待车队确认，已填司机资讯将清空，并通知新旧车队与需求方。",
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "新車隊", "en-US": "New fleet", "zh-CN": "新车队" })}</Label>
              {fleetsQ.isFetched && candidates.length === 0 ? (
                <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-[12px] text-muted-foreground">
                  {t({
                    "zh-TW": "此區域沒有其他可承接的車隊，無法改派。請先於合作方管理新增/啟用車隊。",
                    "en-US": "No other active fleet in this region — reassignment unavailable. Add or activate a fleet first.",
                    "zh-CN": "此区域没有其他可承接的车队，无法改派。请先于合作方管理新增/启用车队。",
                  })}
                </p>
              ) : (
                <Select value={fleetId ?? ""} onChange={(e) => setFleetId(Number(e.target.value) || null)}>
                  <option value="">{t({ "zh-TW": "— 選擇車隊 —", "en-US": "— pick fleet —", "zh-CN": "— 选择车队 —" })}</option>
                  {candidates.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.companyName}（{f.contactPerson ?? "—"}）
                    </option>
                  ))}
                </Select>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "原因（可選）", "en-US": "Reason (optional)", "zh-CN": "原因（可选）" })}</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t({ "zh-TW": "例：原車隊臨時無法承接", "en-US": "e.g. original fleet unavailable", "zh-CN": "例：原车队临时无法承接" })}
              />
            </div>
            {m.error && <p className="text-[12px] text-destructive">{m.error.message}</p>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}
            </Button>
            <Button
              onClick={() => fleetId && m.mutate({ orderId: order.id, newFleetId: fleetId, reason: reason || undefined })}
              disabled={!fleetId || m.isPending}
            >
              {t({ "zh-TW": "確認更換", "en-US": "Reassign", "zh-CN": "确认更换" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── agency 接受 / 退回 ─────────────────────────────────────── */
function AgencyAccept({ orderId, onDone }: { orderId: number; onDone: () => void }) {
  const { t } = useLanguage();
  const m = trpc.quote.accept.useMutation({ onSuccess: onDone });
  return (
    <Button onClick={() => m.mutate({ orderId })} disabled={m.isPending}>
      <Check className="h-3.5 w-3.5" />
      {t({ "zh-TW": "確認報價", "en-US": "Accept quote", "zh-CN": "确认报价" })}
    </Button>
  );
}
/* ── fleet 接受 ─────────────────────────────────────────────── */
function FleetConfirm({ orderId, onDone }: { orderId: number; onDone: () => void }) {
  const { t } = useLanguage();
  const m = trpc.dispatch.fleetConfirm.useMutation({ onSuccess: onDone });
  return (
    <Button onClick={() => m.mutate({ orderId })} disabled={m.isPending}>
      <Check className="h-3.5 w-3.5" />
      {t({ "zh-TW": "確認接單", "en-US": "Accept dispatch", "zh-CN": "确认接单" })}
    </Button>
  );
}

/* ── 報價退回對話框（歷史報價流程限定；fleet 拒絕派遣已移除 — 改派由平台執行）── */
function RejectDialog({
  orderId,
  mode: _mode,
  onDone,
}: {
  orderId: number;
  mode: "quote";
  onDone: () => void;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const m = trpc.quote.reject.useMutation({
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <XCircle className="h-3.5 w-3.5" />
          {t({ "zh-TW": "退回重議", "en-US": "Reject quote", "zh-CN": "退回重议" })}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t({ "zh-TW": "退回報價", "en-US": "Reject quote", "zh-CN": "退回报价" })}
          </DialogTitle>
          <DialogDescription>
            {t({ "zh-TW": "請說明原因（會傳給對方）。", "en-US": "Tell us why (will be shared).", "zh-CN": "请说明原因（会传给对方）。" })}
          </DialogDescription>
        </DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t({ "zh-TW": "原因…", "en-US": "Reason…", "zh-CN": "原因…" })} />
        {m.error && <p className="text-[12px] text-destructive">{m.error.message}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}
          </Button>
          <Button variant="destructive" onClick={() => m.mutate({ orderId, reason })} disabled={!reason || m.isPending}>
            {t({ "zh-TW": "送出", "en-US": "Submit", "zh-CN": "送出" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelButton({ orderId, onDone }: { orderId: number; onDone: () => void }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const m = trpc.order.cancel.useMutation({
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <XCircle className="h-3.5 w-3.5" />
          {t({ "zh-TW": "取消訂單", "en-US": "Cancel", "zh-CN": "取消订单" })}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t({ "zh-TW": "取消訂單", "en-US": "Cancel order", "zh-CN": "取消订单" })}</DialogTitle>
          <DialogDescription>
            {t({ "zh-TW": "請說明取消原因。", "en-US": "Tell us why.", "zh-CN": "请说明取消原因。" })}
          </DialogDescription>
        </DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} />
        {m.error && <p className="text-[12px] text-destructive">{m.error.message}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t({ "zh-TW": "返回", "en-US": "Back", "zh-CN": "返回" })}
          </Button>
          <Button variant="destructive" onClick={() => m.mutate({ id: orderId, reason })} disabled={!reason || m.isPending}>
            {t({ "zh-TW": "確認取消", "en-US": "Confirm", "zh-CN": "确认取消" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────────────────────────────────────────
   司機資訊（20260611 §5）：四角色可見；fleet 於 active 單可填寫/更新。
   出發前 24 小時未填 → fleet 表單紅色警示、平台方紅字提醒。
   ───────────────────────────────────────────────────────────── */
function DriverInfoCard({
  order,
  role,
  onDone,
}: {
  order: NonNullable<OrderGetOutput["order"]>;
  role: "admin" | "dispatcher" | "agency" | "fleet";
  onDone: () => void;
}) {
  const { t } = useLanguage();
  const [driverName, setDriverName] = useState(order.driverName ?? "");
  const [driverPhone, setDriverPhone] = useState(order.driverPhone ?? "");
  const [vehiclePlate, setVehiclePlate] = useState(order.vehiclePlate ?? "");
  // 2026-08-26：司機即時通訊（選填）＋常用司機名冊
  const [driverWhatsapp, setDriverWhatsapp] = useState(order.driverWhatsapp ?? "");
  const [driverLine, setDriverLine] = useState(order.driverLine ?? "");
  const [driverWechat, setDriverWechat] = useState(order.driverWechat ?? "");
  const [saveToRoster, setSaveToRoster] = useState(false);
  const isFleet = role === "fleet";
  const rosterQ = trpc.fleetDriver.list.useQuery(undefined, { enabled: isFleet && order.status === "active" });
  const roster = rosterQ.data ?? [];
  const utils = trpc.useUtils();
  const m = trpc.dispatch.submitDriverInfo.useMutation({
    onSuccess: () => {
      void utils.fleetDriver.list.invalidate();
      onDone();
    },
  });

  const pickFromRoster = (id: string) => {
    const d = roster.find((x) => String(x.id) === id);
    if (!d) return;
    setDriverName(d.name);
    setDriverPhone(d.phone);
    setVehiclePlate(d.vehiclePlate ?? "");
    setDriverWhatsapp(d.whatsapp ?? "");
    setDriverLine(d.line ?? "");
    setDriverWechat(d.wechat ?? "");
  };

  const submitted = !!order.driverInfoSubmittedAt;
  const canEdit = role === "fleet" && order.status === "active";
  const overdue = isDriverInfoOverdue(order.status, order.pickupTime, order.driverInfoSubmittedAt, Date.now());

  // 178 反饋 C4：司機資訊變更記錄（四角色一致；僅當有過修改（≥2 筆）才顯示小節）
  const historyQ = trpc.order.driverInfoHistory.useQuery({ orderId: order.id }, { enabled: submitted });
  const history = historyQ.data ?? [];

  // 尚未派遣（無車隊）且沒填過 → 不顯示，避免噪音
  if (!order.fleetId && !submitted) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <UserRound className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
          {t({ "zh-TW": "司機資訊", "en-US": "Driver info", "zh-CN": "司机资讯" })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {canEdit ? (
          <>
            {overdue && (
              // 色票在 :root 而非 @theme，named utility（text-destructive 等）不會生效 — 紅色警示用 var() 寫法
              <div className="flex items-start gap-2 rounded border border-[var(--color-destructive)]/40 bg-[var(--color-destructive-soft)] px-2.5 py-2 text-[12px] text-[var(--color-destructive)]">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {t({
                  "zh-TW": "出發前 24 小時須填妥司機資訊，請立即回報。",
                  "en-US": "Driver info must be submitted 24 hours before departure — please report now.",
                  "zh-CN": "出发前 24 小时须填妥司机资讯，请立即回报。",
                })}
              </div>
            )}
            {submitted && (
              <p className="text-[12px] text-muted-foreground">
                {t({ "zh-TW": "上次提交：", "en-US": "Last submitted: ", "zh-CN": "上次提交：" })}
                {fmtDateTime(order.driverInfoSubmittedAt)}
                {t({ "zh-TW": "（可修改後重新提交）", "en-US": " (edit and resubmit anytime)", "zh-CN": "（可修改后重新提交）" })}
              </p>
            )}
            {roster.length > 0 && (
              <div className="space-y-1.5">
                <Label>{t({ "zh-TW": "從常用司機選取", "en-US": "Pick from saved drivers", "zh-CN": "从常用司机选取" })}</Label>
                <Select defaultValue="" onChange={(e) => pickFromRoster(e.target.value)}>
                  <option value="">{t({ "zh-TW": "— 手動輸入 —", "en-US": "— Enter manually —", "zh-CN": "— 手动输入 —" })}</option>
                  {roster.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} · {d.phone}
                      {d.vehiclePlate ? ` · ${d.vehiclePlate}` : ""}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "司機姓名", "en-US": "Driver name", "zh-CN": "司机姓名" })}</Label>
              <Input value={driverName} onChange={(e) => setDriverName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "司機電話", "en-US": "Driver phone", "zh-CN": "司机电话" })}</Label>
              <Input value={driverPhone} onChange={(e) => setDriverPhone(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "車牌號碼", "en-US": "Vehicle plate", "zh-CN": "车牌号码" })}</Label>
              <Input value={vehiclePlate} onChange={(e) => setVehiclePlate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px] text-muted-foreground">
                {t({ "zh-TW": "司機即時通訊（選填，至少填一項更方便旅客聯繫）", "en-US": "Driver messaging (optional)", "zh-CN": "司机即时通讯（选填）" })}
              </Label>
              <div className="grid grid-cols-1 gap-2">
                <Input placeholder="WhatsApp" value={driverWhatsapp} onChange={(e) => setDriverWhatsapp(e.target.value)} />
                <Input placeholder="LINE" value={driverLine} onChange={(e) => setDriverLine(e.target.value)} />
                <Input placeholder="WeChat" value={driverWechat} onChange={(e) => setDriverWechat(e.target.value)} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                className="accent-[var(--color-primary)]"
                checked={saveToRoster}
                onChange={(e) => setSaveToRoster(e.target.checked)}
              />
              {t({ "zh-TW": "加入常用司機資訊（下次可直接選取）", "en-US": "Save to my drivers (reuse next time)", "zh-CN": "加入常用司机资讯（下次可直接选取）" })}
            </label>
            {m.error && <p className="text-[12px] text-destructive">{m.error.message}</p>}
            <Button
              className="w-full"
              onClick={() =>
                m.mutate({
                  orderId: order.id,
                  driverName: driverName.trim(),
                  driverPhone: driverPhone.trim(),
                  vehiclePlate: vehiclePlate.trim(),
                  driverWhatsapp: driverWhatsapp.trim(),
                  driverLine: driverLine.trim(),
                  driverWechat: driverWechat.trim(),
                  saveToRoster,
                })
              }
              disabled={!driverName.trim() || !driverPhone.trim() || !vehiclePlate.trim() || m.isPending}
            >
              <Check className="h-3.5 w-3.5" />
              {submitted
                ? t({ "zh-TW": "更新司機資訊", "en-US": "Update driver info", "zh-CN": "更新司机资讯" })
                : t({ "zh-TW": "提交司機資訊", "en-US": "Submit driver info", "zh-CN": "提交司机资讯" })}
            </Button>
          </>
        ) : submitted ? (
          <div className="space-y-2 text-[13px]">
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">{t({ "zh-TW": "司機姓名", "en-US": "Driver", "zh-CN": "司机姓名" })}</span>
              <span className="font-medium">{order.driverName}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">{t({ "zh-TW": "司機電話", "en-US": "Phone", "zh-CN": "司机电话" })}</span>
              <span className="numeric font-medium">{order.driverPhone}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">{t({ "zh-TW": "車牌號碼", "en-US": "Plate", "zh-CN": "车牌号码" })}</span>
              <span className="font-mono font-medium uppercase">{order.vehiclePlate}</span>
            </div>
            {([
              ["WhatsApp", order.driverWhatsapp],
              ["LINE", order.driverLine],
              ["WeChat", order.driverWechat],
            ] as const)
              .filter(([, v]) => !!v)
              .map(([label, v]) => (
                <div key={label} className="flex justify-between gap-2">
                  <span className="text-muted-foreground">{label}</span>
                  <span className="font-medium">{v}</span>
                </div>
              ))}
            <p className="pt-1 text-[12px] text-muted-foreground">
              {t({ "zh-TW": "提交時間：", "en-US": "Submitted: ", "zh-CN": "提交时间：" })}
              {fmtDateTime(order.driverInfoSubmittedAt)}
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            <p className="text-[13px] italic text-muted-foreground">
              {t({ "zh-TW": "尚未回報", "en-US": "Not reported yet", "zh-CN": "尚未回报" })}
            </p>
            {/* 178 反饋 C3：逾期警告放寬 — 需求方（agency）也要看得到，不只平台方 */}
            {overdue && (role === "dispatcher" || role === "admin" || role === "agency") && (
              <p className="flex items-center gap-1.5 text-[12px] text-[var(--color-destructive)]">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                {role === "agency"
                  ? t({
                      "zh-TW": "車隊逾期未回報司機資訊，平台正在跟進。",
                      "en-US": "The fleet is overdue on reporting driver info — the platform is following up.",
                      "zh-CN": "车队逾期未回报司机资讯，平台正在跟进。",
                    })
                  : t({
                      "zh-TW": "出發不足 24 小時仍未回報，請聯繫車隊。",
                      "en-US": "Departure within 24h and still unreported — contact the fleet.",
                      "zh-CN": "出发不足 24 小时仍未回报，请联系车队。",
                    })}
              </p>
            )}
          </div>
        )}
        {/* 178 反饋 C4：變更記錄（四角色一致）— 有過修改（≥2 筆）才顯示，每筆：時間 · 姓名 · 電話 · 車牌 */}
        {history.length >= 2 && (
          <div className="space-y-1.5 pt-1">
            <Separator />
            <p className="pt-1 text-[12px] font-medium text-muted-foreground">
              {t({ "zh-TW": "變更記錄", "en-US": "Change history", "zh-CN": "变更记录" })}
            </p>
            <ul className="space-y-1">
              {history.map((h, i) => (
                <li key={`${h.at}-${i}`} className="text-[12px] text-muted-foreground">
                  {fmtDateTime(h.at)} · {h.driverName ?? "—"} · {h.driverPhone ?? "—"} · {h.vehiclePlate ?? "—"}
                  {[
                    h.driverWhatsapp ? `WhatsApp ${h.driverWhatsapp}` : null,
                    h.driverLine ? `LINE ${h.driverLine}` : null,
                    h.driverWechat ? `WeChat ${h.driverWechat}` : null,
                  ].filter(Boolean).map((x) => ` · ${x}`).join("")}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CompleteButton({ orderId, pickupTime, onDone }: { orderId: number; pickupTime: number; onDone: () => void }) {
  const { t } = useLanguage();
  const m = trpc.dispatch.complete.useMutation({ onSuccess: onDone });
  const notYet = !canMarkCompleteAt(pickupTime, Date.now());
  return (
    <Button
      onClick={() => m.mutate({ orderId })}
      disabled={m.isPending || notYet}
      title={notYet ? t({ "zh-TW": "服務日尚未到，無法標示完成", "en-US": "Service date not reached yet", "zh-CN": "服务日尚未到，无法标示完成" }) : undefined}
    >
      <Check className="h-3.5 w-3.5" />
      {t({ "zh-TW": "標示完成", "en-US": "Mark complete", "zh-CN": "标示完成" })}
    </Button>
  );
}
